/**
 * Client-perceived timing → PostHog.
 *
 * Measures how long a user actually waits between a gesture and the result
 * (panel painted, audio audible), as wall-clock ms via performance.now()
 * (relative to navigation start), rounded. Captures are best-effort and never
 * throw into callers.
 *
 * Two shapes:
 *  - markStart(name) … markEnd(name, event, props): when start and end live in
 *    different components (e.g. word click in StoryReaderPage, panel render in
 *    LearnerExplanation).
 *  - now() + captureSince(t0, event, props): when start and end are in the same
 *    function (e.g. karaoke audio play, sentence translation fetch).
 */
import posthog from './posthog';

const starts = new Map();

export function now() {
  return typeof performance !== 'undefined' ? performance.now() : null;
}

export function markStart(name) {
  const t = now();
  if (t != null) starts.set(name, t);
}

export function markEnd(name, event, props = {}) {
  const t0 = starts.get(name);
  if (t0 == null) return null; // no matching start (e.g. history nav) — skip
  starts.delete(name);
  return captureSince(t0, event, props);
}

export function captureSince(t0, event, props = {}) {
  if (t0 == null) return null;
  const durationMs = Math.round(performance.now() - t0);
  try {
    posthog?.capture?.(event, { ...props, durationMs });
  } catch (_) {
    /* best effort — never break the UI for a metric */
  }
  return durationMs;
}
